import * as React from 'react';
import { __ } from 'react-i18n';
import styled from 'styled-components';
import { rem } from 'polished';
import { InfoText, InfoType } from '../../General/InfoText';
import {
  FlexCol,
  FlexRowCenterVertical,
  FlexRowCenter,
} from '../../../Defaults/Flexbox/Flexbox';
import { TextP } from '../../../Defaults/Text';
import { Button } from '../../General/Button';
import { SummaryValue } from './SummaryValue';
import { AcceptTermsCheckbox } from './AcceptTermsCheckbox';
import { FinalValue } from './FinalValue';
import { getCartSummaryInfo } from '../../../../utilities/selectors';
import { formatPriceToString, prop } from '../../../../utilities';
import { getZemplinCartSummaryInfo } from '../../selectors';
import { Popup } from '../../General/Popup';

interface Props {
  handleStepChange: (step: number) => void;
  data: any;
  handleAcceptTermsChange: (e: any) => void;
  isFetching: boolean;
  user: any;
  isDemand: boolean;
  hasPartlyOnStockItem: number;
  cartB2BDiscount: number;
}

function ThirdStep({
  handleStepChange,
  data,
  handleAcceptTermsChange,
  isFetching,
  user,
  isDemand,
  hasPartlyOnStockItem,
  cartB2BDiscount,
}: Props) {
  const isB2B = user && user.b2b;
  const {
    totalPrice,
    totalPriceWithoutVat,
    vatTotal,
    currency,
    deliveryPrice,
    paymentPrice,
    totalItemsPriceWithoutVat,
    totalItemsPrice,
  } = getCartSummaryInfo(data, user);
  const {
    vatGroup0Total,
    vatGroup0TotalWithDph,
    vatGroup20Total,
    vatGroup20TotalDph,
    vatGroup20TotalWithDph,
  } = getZemplinCartSummaryInfo(data);
  const termsAccept = prop(data, 'terms_accept');
  const canOrder =
    termsAccept && prop(data, 'delivery') && prop(data, 'payment');
  const { items } = data;
  const hasNullPriceItems = items.some(i => i.sum_price === 0);
  return (
    <>
      <DesktopFields>
        <SummaryValue
          borderBottom={
            cartB2BDiscount && cartB2BDiscount > 0
              ? false
              : deliveryPrice
              ? false
              : paymentPrice > 0
              ? false
              : true
          }
          title={isB2B ? __('Cena tovaru bez DPH') : __('Cena tovaru s DPH')}
          value={formatPriceToString(
            isB2B ? totalItemsPriceWithoutVat : totalItemsPrice,
            currency,
          )}
        />
        {cartB2BDiscount && cartB2BDiscount > 0 ? (
          <SummaryValue
            order={1}
            borderBottom={
              deliveryPrice ? false : paymentPrice > 0 ? false : true
            }
            title={__('Nákupom cez eshop ste ušetrili')}
            value={`-${formatPriceToString(cartB2BDiscount, currency)}`}
          />
        ) : null}
        {deliveryPrice ? (
          <SummaryValue
            borderBottom={paymentPrice > 0 ? false : true}
            title={isB2B ? __('Doprava bez DPH') : __('Doprava s DPH')}
            value={formatPriceToString(deliveryPrice, currency)}
          />
        ) : null}
        {paymentPrice > 0 && (
          <SummaryValue
            borderBottom={true}
            title={isB2B ? __('Platba bez DPH') : __('Platba s DPH')}
            value={formatPriceToString(paymentPrice, currency)}
          />
        )}
        {vatGroup20TotalDph && (
          <>
            {' '}
            <SummaryValue
              title={__('Základ dane pre 20 %')}
              value={formatPriceToString(vatGroup20Total, currency)}
            />
            <SummaryValue
              title={__('DPH 20 %')}
              value={formatPriceToString(vatGroup20TotalDph, currency)}
            />
            <SummaryValue
              borderBottom={true}
              title={__('Cena s DPH')}
              value={formatPriceToString(vatGroup20TotalWithDph, currency)}
            />
          </>
        )}
        {vatGroup0TotalWithDph && (
          <>
            {' '}
            <SummaryValue
              title={__('Základ dane pre 0 %')}
              value={formatPriceToString(vatGroup0Total, currency)}
            />
            <SummaryValue
              title={__('DPH 0 %')}
              value={formatPriceToString('0.00', currency)}
            />
            <SummaryValue
              borderBottom={true}
              title={__('Cena s DPH')}
              value={formatPriceToString(vatGroup0TotalWithDph, currency)}
            />
          </>
        )}
        <FinalValue
          order={3}
          title={__('Celkom k úhrade s DPH')}
          value={formatPriceToString(totalPrice, currency)}
          showInfo={hasPartlyOnStockItem}
        />
      </DesktopFields>
      <MobileFields>
        <SummaryValue
          title={__('Cena tovaru bez DPH')}
          value={formatPriceToString(totalItemsPriceWithoutVat, currency)}
        />
        <SummaryValue
          title={__('DPH 20 %')}
          value={formatPriceToString(vatTotal, currency)}
        />
        <SummaryValue
          title={__('Cena s DPH')}
          value={formatPriceToString(totalPrice, currency)}
        />
        <SummaryValue
          borderBottom={true}
          title={__('Doprava bez DPH')}
          value={formatPriceToString(deliveryPrice, currency)}
        />
        <FinalValue
          title={__('Celkom k úhrade s DPH')}
          value={formatPriceToString(totalPrice, currency)}
          showInfo={hasPartlyOnStockItem}
        />
      </MobileFields>
      {hasPartlyOnStockItem ? (
        <InfoText
          type={InfoType.INFO}
          noIcon={true}
          info={__(
            'Cena produktov, ktoré nie sú skladom, nie je započítaná do hodnoty objednávky a ceny dopravy.',
          )}
          marginBottom={32}
        />
      ) : null}
      {/* TURNED OFF */}
      {/* <InfoText
        type={InfoType.ERROR}
        info={
          'Máte nezaplatené faktúry po lehote splatnosti v hodnote 9 999 €. Tovar vám nebude expedovaný. Prosíme vás o úhradu faktúr a kontaktujte svojho obchodného zástupcu, aby ste sa dohodli na ďalšom postupe.'
        }
      /> */}
      <AcceptTermsCheckbox
        name="acceptTerms"
        onClick={handleAcceptTermsChange}
        selected={termsAccept}
      />
      <NextButton
        disabled={!canOrder || isFetching}
        // tslint:disable-next-line:jsx-no-lambda
        onClick={() => handleStepChange(4)}
      >
        {isFetching ? (
          <FlexRowCenter>
            <Loader className="marginLeft" small={true} />
          </FlexRowCenter>
        ) : isDemand ? (
          __('Odoslať dopyt')
        ) : (
          __('Objednávka s povinnosťou platby')
        )}
      </NextButton>
      <BackButton
        // tslint:disable-next-line:jsx-no-lambda
        onClick={() => handleStepChange(2)}
      >
        {__('Späť')}
      </BackButton>
    </>
  );
}

export const Loader = styled.div<{ className?: string; small?: boolean }>`
  width: ${({ small }) => (small ? rem(20) : rem(50))};
  height: ${({ small }) => (small ? rem(20) : rem(50))};
  border: ${({ small }) =>
    small ? `${rem(4)} solid #f9f9f9` : `${rem(10)} solid #f9f9f9`};
  border-top: ${({ small, theme }) =>
    small
      ? `${rem(4)} solid ${theme.blogColors.primary}`
      : `${rem(10)} solid ${theme.blogColors.primary}`};
  margin: 0;
  border-radius: 50%;
  animation: spin 1.5s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const NextButton = styled(Button)`
  height: ${rem(48)};
  margin: ${rem(15)} 0 0;
  padding: ${rem(8)};
  width: 100%;
  max-width: ${rem(320)};
  background-color: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.black};
  font-size: ${rem(14)};
  border-radius: ${rem(2)};
  margin: 0 0 ${rem(16)};

  &:disabled {
    background-color: #f9f9f9;
    color: #cccccc;
  }
`;

const BackButton = styled(NextButton)`
  background-color: ${({ theme }) => theme.colors.white};
  border: ${({ theme }) => theme.borders.primary};
  margin-top: 0;
`;

const DesktopFields = styled(FlexCol)`
  ${({ theme }) => theme.mediab.l925`
    display: none;
  `}
`;

const MobileFields = styled(FlexCol)`
  display: none;
  margin-bottom: ${rem(16)};

  ${({ theme }) => theme.mediab.l925`
    display: flex;
  `}
`;

export { ThirdStep };
